import { useCallback, useEffect, useRef, useState } from 'react';

import { Empty } from './ui';
import { api, ApiError, getToken } from '../lib/api';

/**
 * KnowledgeIQ source groups, managed per server per client.
 *
 * The instance picker is not a convenience — it is the point. `knowledgeiq-abc`
 * on Dev & Test and `knowledgeiq-adq` on CA-001 are two different clients'
 * knowledge bases, each with its own container and its own API key, and nothing
 * here crosses between them. Choosing the instance first makes that structural
 * rather than something the reader has to remember.
 */

type JobStatus = 'processing' | 'ready' | 'error';

interface Job {
  status: JobStatus;
  detail: string;
  pages_done: number;
  documents_done: number;
}

interface SourceGroup {
  id: string;
  label: string;
  type: 'documents' | 'website';
  documents?: string[];
  url?: string;
  maxPages?: number;
  job?: Job;
}

interface Instance {
  deploymentId: string;
  name: string;
  version: string | null;
  server: string;
  serverId: string;
  client: string | null;
  clientName: string | null;
  containers: number;
  sshEnabled: boolean;
  hasSpec: boolean;
  supportsSources: boolean;
}

/** How often a processing job is re-checked, and when to stop trying. */
const POLL_MS = 2500;
const POLL_CEILING_MS = 10 * 60 * 1000;

const EXTENSIONS = '.pdf,.docx,.txt,.md';

function why(i: Instance): string | null {
  // Three different reasons, reported as three different sentences. "Unavailable"
  // alone sends someone to the wrong place: a stopped stack, an unreachable host
  // and an older build each need a different action.
  if (!i.sshEnabled) return 'The collector has no credentials for this server, so it cannot be reached.';
  if (!i.hasSpec) return 'No API spec captured yet — run API discovery from this page first.';
  if (!i.supportsSources) {
    // Two causes, and this used to assert only the first. The stored spec is
    // refreshed by "Refresh APIs", not by the 60-second cycle, so an instance
    // upgraded an hour ago still answers from its old document — which is
    // exactly what happened to knowledgeiq-adq on CA-001. Blaming the build
    // when our copy is out of date sends people to the wrong place.
    return 'No /sources in the spec PlatformIQ holds for this instance. Either it runs an older KnowledgeIQ build, or the spec predates an upgrade — press "Refresh APIs" on the Products page first.';
  }
  return null;
}

function StatusBadge({ job }: { job?: Job }) {
  if (!job) return <span className="tag">no status</span>;
  if (job.status === 'processing') {
    return (
      <span className="tag tag-warn" title={job.detail}>
        <span className="spinner" aria-hidden="true" /> processing
      </span>
    );
  }
  if (job.status === 'error') {
    return (
      <span className="tag tag-bad" title={job.detail}>
        error
      </span>
    );
  }
  return (
    <span className="tag tag-ok" title={job.detail}>
      ready
    </span>
  );
}

export function SourceGroups() {
  const [instances, setInstances] = useState<Instance[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [sources, setSources] = useState<SourceGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api<Instance[]>('/knowledgeiq/instances')
      .then((rows) => {
        setInstances(rows);
        // Auto-select only when there is exactly one usable instance. Picking
        // one of several on the reader's behalf would mean an action could land
        // on a client they did not choose.
        const usable = rows.filter((r) => why(r) === null);
        if (usable.length === 1) setChosen(usable[0].deploymentId);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not list instances'));
  }, []);

  const loadSources = useCallback(
    async (deploymentId: string) => {
      setError(null);
      try {
        const r = await api<{ sources: SourceGroup[] }>(`/knowledgeiq/${deploymentId}/sources`);
        setSources(r.sources ?? []);
      } catch (e) {
        setSources(null);
        setError(e instanceof Error ? e.message : 'Could not load source groups');
      }
    },
    [],
  );

  useEffect(() => {
    if (chosen) void loadSources(chosen);
    else setSources(null);
  }, [chosen, loadSources]);

  /**
   * Re-reads one group until its job settles.
   *
   * Polls that row alone rather than the whole list: a 25MB ingest can take
   * minutes, and re-listing every group on a client's instance every 2.5s for
   * that long is load on their production for no extra information.
   *
   * The ceiling matters. Without it a job wedged in "processing" leaves a
   * browser tab calling a client's API every few seconds for as long as it stays
   * open, and nobody would notice.
   */
  const poll = useCallback(
    (deploymentId: string, id: string) => {
      const startedAt = Date.now();
      const tick = async () => {
        if (Date.now() - startedAt > POLL_CEILING_MS) {
          setSources((cur) =>
            cur?.map((s) =>
              s.id === id
                ? { ...s, job: { status: 'error', detail: 'Still processing after 10 minutes — stopped watching. Reload to check again.', pages_done: 0, documents_done: 0 } }
                : s,
            ) ?? cur,
          );
          return;
        }
        try {
          const fresh = await api<SourceGroup>(`/knowledgeiq/${deploymentId}/sources/${encodeURIComponent(id)}`);
          setSources((cur) => cur?.map((s) => (s.id === id ? fresh : s)) ?? cur);
          if (fresh.job?.status === 'processing') setTimeout(tick, POLL_MS);
        } catch {
          // A failed poll is not a failed job. Keep watching — the instance may
          // be mid-restart — and let the ceiling end it if it never recovers.
          setTimeout(tick, POLL_MS);
        }
      };
      setTimeout(tick, POLL_MS);
    },
    [],
  );

  // Anything already processing when the list loads is picked up, so a job
  // someone else started is watched too rather than sitting at a stale spinner.
  const watched = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!chosen || !sources) return;
    for (const s of sources) {
      const key = `${chosen}/${s.id}`;
      if (s.job?.status === 'processing' && !watched.current.has(key)) {
        watched.current.add(key);
        poll(chosen, s.id);
      }
      if (s.job?.status !== 'processing') watched.current.delete(key);
    }
  }, [chosen, sources, poll]);

  async function act(id: string, run: () => Promise<unknown>, thenPoll = true) {
    if (!chosen) return;
    setBusy(id);
    setError(null);
    try {
      await run();
      await loadSources(chosen);
      if (thenPoll) poll(chosen, id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'That did not work');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Uploads with fetch rather than the api() helper.
   *
   * FormData must set its own multipart Content-Type, including the boundary —
   * so the usual JSON header the helper applies would make the body unparseable
   * on the far side. The token is attached by hand for the same reason.
   */
  async function upload(id: string, files: FileList) {
    if (!chosen || files.length === 0) return;
    const body = new FormData();
    for (const f of Array.from(files)) body.append('files', f);

    await act(id, async () => {
      const res = await fetch(`/api/v1/knowledgeiq/${chosen}/sources/${encodeURIComponent(id)}/documents`, {
        method: 'POST',
        headers: { authorization: `Bearer ${getToken() ?? ''}` },
        body,
      });
      if (!res.ok) {
        const text = await res.text();
        let message = `Upload failed (${res.status})`;
        try {
          message = (JSON.parse(text) as { message?: string }).message ?? message;
        } catch {
          /* keep the status */
        }
        throw new Error(message);
      }
    });
  }

  if (error && !instances) return <p className="form-error">{error}</p>;
  if (!instances) return <p className="muted small">Loading instances…</p>;
  if (instances.length === 0) {
    return <Empty>No KnowledgeIQ deployments are known yet.</Empty>;
  }

  const current = instances.find((i) => i.deploymentId === chosen) ?? null;
  const blocked = current ? why(current) : null;

  return (
    <div className="sources">
      <div className="sources-pick">
        <label htmlFor="kiq-instance" className="muted small">
          Instance
        </label>
        <select
          id="kiq-instance"
          value={chosen ?? ''}
          onChange={(e) => setChosen(e.target.value || null)}
        >
          <option value="">Choose a server and client…</option>
          {instances.map((i) => (
            <option key={i.deploymentId} value={i.deploymentId}>
              {i.client ?? 'COMMON'} · {i.server} · {i.name}
              {why(i) ? ' (unavailable)' : ''}
            </option>
          ))}
        </select>
      </div>

      {!current && (
        <p className="muted small">
          Each KnowledgeIQ instance is a separate knowledge base belonging to one client on one
          server. Choose which one to manage.
        </p>
      )}

      {current && blocked && <p className="notice">{blocked}</p>}

      {current && !blocked && (
        <>
          <div className="sources-head">
            <span className="muted small">
              {current.clientName ?? 'COMMON'} · {current.server} · <code>{current.name}</code>
              {current.version && <> · {current.version}</>}
            </span>
            <button type="button" className="ghost small" onClick={() => setCreating((c) => !c)}>
              {creating ? 'Cancel' : '+ New source group'}
            </button>
          </div>

          {creating && (
            <NewSourceForm
              onCancel={() => setCreating(false)}
              onCreate={async (payload) => {
                await api(`/knowledgeiq/${chosen}/sources`, {
                  method: 'POST',
                  body: JSON.stringify(payload),
                });
                setCreating(false);
                await loadSources(chosen!);
                // A website group starts crawling immediately, so it is already
                // processing by the time the list comes back.
                if (payload.type === 'website') poll(chosen!, payload.id);
              }}
            />
          )}

          {error && <p className="form-error">{error}</p>}

          {sources === null ? (
            <p className="muted small">Loading source groups…</p>
          ) : sources.length === 0 ? (
            <Empty>No source groups yet on this instance.</Empty>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">Id</th>
                    <th scope="col">Label</th>
                    <th scope="col">Type</th>
                    <th scope="col">Contents</th>
                    <th scope="col">Status</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((s) => {
                    const processing = s.job?.status === 'processing';
                    const locked = processing || busy === s.id;
                    return (
                      <tr key={s.id}>
                        <td>
                          <code>{s.id}</code>
                        </td>
                        <td>{s.label}</td>
                        <td className="small">{s.type}</td>
                        <td className="small">
                          {s.type === 'documents' ? (
                            <>
                              {s.documents?.length ?? 0} document{(s.documents?.length ?? 0) === 1 ? '' : 's'}
                              {s.documents && s.documents.length > 0 && (
                                <div className="muted small" title={s.documents.join(', ')}>
                                  {s.documents.slice(0, 3).join(', ')}
                                  {s.documents.length > 3 && ` +${s.documents.length - 3}`}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <a href={s.url} target="_blank" rel="noreferrer noopener">
                                {s.url}
                              </a>
                              <div className="muted small">
                                {s.job?.pages_done ? `${s.job.pages_done} pages` : 'not crawled yet'}
                                {s.maxPages ? ` · max ${s.maxPages}` : ''}
                              </div>
                            </>
                          )}
                        </td>
                        <td>
                          <StatusBadge job={s.job} />
                          {/* The failure reason in full. A spinner that turns
                              into a bare "error" badge tells someone that it
                              broke and nothing about what to do. */}
                          {s.job?.status === 'error' && s.job.detail && (
                            <div className="form-error small">{s.job.detail}</div>
                          )}
                          {processing && s.job?.detail && (
                            <div className="muted small">{s.job.detail}</div>
                          )}
                        </td>
                        <td>
                          <div className="row-actions">
                            {s.type === 'documents' && (
                              <label className={locked ? 'upload-btn disabled' : 'upload-btn'}>
                                {s.job?.status === 'error' ? 'Re-upload' : 'Upload'}
                                <input
                                  type="file"
                                  multiple
                                  accept={EXTENSIONS}
                                  disabled={locked}
                                  onChange={(e) => {
                                    if (e.target.files) void upload(s.id, e.target.files);
                                    // Cleared so choosing the same file twice
                                    // in a row fires onChange the second time.
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            )}
                            {s.type === 'website' && (
                              <button
                                type="button"
                                className="ghost small"
                                disabled={locked}
                                onClick={() =>
                                  void act(s.id, () =>
                                    api(`/knowledgeiq/${chosen}/sources/${encodeURIComponent(s.id)}/recrawl`, {
                                      method: 'POST',
                                    }),
                                  )
                                }
                              >
                                {s.job?.status === 'error' ? 'Retry crawl' : 'Re-crawl'}
                              </button>
                            )}
                            <DeleteSource
                              id={s.id}
                              label={s.label}
                              disabled={locked}
                              onDelete={() =>
                                act(
                                  s.id,
                                  () =>
                                    api(`/knowledgeiq/${chosen}/sources/${encodeURIComponent(s.id)}`, {
                                      method: 'DELETE',
                                    }),
                                  false,
                                )
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="muted small">
            Reached by running curl on {current.server} against the instance's own container — the
            same path PlatformIQ uses to read every product's API. Uploads and crawls run as
            background jobs; this page polls each row until its job settles. Every action is audited.
          </p>
        </>
      )}
    </div>
  );
}

/** Confirm in place. Deleting a group wipes everything indexed under it. */
function DeleteSource({
  id,
  label,
  disabled,
  onDelete,
}: {
  id: string;
  label: string;
  disabled: boolean;
  onDelete: () => Promise<void>;
}) {
  const [asking, setAsking] = useState(false);

  if (asking) {
    return (
      <span className="confirm">
        <span className="small">
          Delete <strong>{label}</strong> and everything indexed under it?
        </span>
        <button
          type="button"
          className="ghost small danger"
          onClick={() => {
            setAsking(false);
            void onDelete();
          }}
        >
          Delete
        </button>
        <button type="button" className="ghost small" onClick={() => setAsking(false)}>
          No
        </button>
      </span>
    );
  }
  return (
    <button
      type="button"
      className="ghost small"
      disabled={disabled}
      title={`Delete source group ${id}`}
      onClick={() => setAsking(true)}
    >
      Delete
    </button>
  );
}

function NewSourceForm({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (payload: {
    id: string;
    label: string;
    type: 'documents' | 'website';
    url?: string;
    maxPages?: number;
  }) => Promise<void>;
}) {
  const [id, setId] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState<'documents' | 'website'>('documents');
  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idOk = /^[A-Za-z0-9_-]+$/.test(id);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await onCreate({
        id,
        label,
        type,
        ...(type === 'website'
          ? { url, ...(maxPages ? { maxPages: Number(maxPages) } : {}) }
          : {}),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create that source group');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="new-source">
      <div className="field-row">
        <label>
          <span className="muted small">Id</span>
          <input
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="A"
            maxLength={64}
            aria-invalid={id !== '' && !idOk}
          />
        </label>
        <label>
          <span className="muted small">Label</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Policies" maxLength={200} />
        </label>
      </div>

      {id !== '' && !idOk && (
        <p className="form-error small">
          Letters, numbers, hyphens and underscores only — the service rejects anything else.
        </p>
      )}

      <fieldset className="type-pick">
        <legend className="muted small">Type</legend>
        <label className="check">
          <input type="radio" checked={type === 'documents'} onChange={() => setType('documents')} />
          Documents — created empty, then upload files into it
        </label>
        <label className="check">
          <input type="radio" checked={type === 'website'} onChange={() => setType('website')} />
          Website — starts crawling as soon as it is created
        </label>
      </fieldset>

      {type === 'website' && (
        <div className="field-row">
          <label>
            <span className="muted small">URL</span>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              inputMode="url"
            />
          </label>
          <label>
            <span className="muted small">Max pages</span>
            <input
              value={maxPages}
              onChange={(e) => setMaxPages(e.target.value.replace(/\D/g, ''))}
              placeholder="200"
              inputMode="numeric"
            />
          </label>
        </div>
      )}

      {error && <p className="form-error small">{error}</p>}

      <div className="row-actions">
        <button
          type="button"
          disabled={busy || !idOk || label.trim() === '' || (type === 'website' && url.trim() === '')}
          onClick={() => void submit()}
        >
          {busy ? 'Creating…' : 'Create'}
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
